import $ from 'jquery'

/**
 * Vidéos de fond (.bg-video) chargées à la demande, sur les deux flexibles
 * qui en ont encore : la section CTA newsletter (hover uniquement, cf.
 * cta-newsletter-contact.scss) et le fond animé de page-break (autoplay dès
 * l'approche du viewport, cf. page-break.twig). On ne les télécharge pas au
 * chargement de la page : les .bg-video hover sont chargées au premier
 * survol du conteneur, les .bg-video autoplay via un IntersectionObserver
 * dès qu'elles approchent du viewport (tous appareils).
 */
export class VideoLoading {
  constructor () {
    // Conteneur susceptible de porter une vidéo de hover : section CTA newsletter.
    this.containers = '.cta-newsletter-contact .container'

    // Sans hover (tactile), la vidéo hover n'est jamais visible : rien à charger.
    // Testé à chaque événement (et non une seule fois) car la capacité peut
    // changer en cours de session (2-en-1 tablette -> clavier/souris), comme
    // la media query CSS.
    this.hoverMedia = window.matchMedia('(hover: hover)')

    $(document)
      .on('mouseenter', this.containers, this.play.bind(this))
      .on('mouseleave', this.containers, this.pause.bind(this))

    $(this.prefetch.bind(this))
    $(document).ajaxComplete(this.prefetch.bind(this))
  }

  /**
   * Observe les .bg-video[data-src] et les charge dès qu'elles approchent du
   * viewport (marge d'un viewport), sans attendre le survol.
   *
   * - bg-video[autoplay] (ex. page-break) : observées sur tous les appareils
   *   (le fond animé est visible même sur tactile) ; lecture déclenchée
   *   explicitement après hydratation.
   * - bg-video sans autoplay (ex. cta-newsletter-contact) : observées
   *   uniquement si hover disponible (masquées par CSS sur tactile).
   */
  prefetch () {
    if (!this.observer) {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return
          }

          this.observer.unobserve(entry.target)
          this.hydrate(entry.target, 'auto')

          // bg-video autoplay (ex. page-break) : lecture dès que le src est posé.
          if (entry.target.classList.contains('autoplay')) {
            entry.target.muted = true
            const playing = entry.target.play()
            if (playing) {
              playing.catch(() => {})
            }
          }
        })
      }, {rootMargin: '100% 0px'})
    }

    // bg-video autoplay : toujours observé (indépendant de la capacité hover).
    document.querySelectorAll('video.bg-video[data-src][autoplay]').forEach((video) => {
      this.observer.observe(video)
    })

    // bg-video hover : uniquement si hover disponible.
    if (this.hoverMedia.matches) {
      document.querySelectorAll('video.bg-video[data-src]:not([autoplay])').forEach((video) => {
        this.observer.observe(video)
      })
    }
  }

  play (e) {
    if (!this.hoverMedia.matches) {
      return
    }

    const video = this.getVideo(e.currentTarget)

    if (!video) {
      return
    }

    window.clearTimeout(video.videoPauseTimer)

    // Premier survol : on pose enfin le src (servi par le cache HTTP si un
    // prefetch a déjà eu lieu, sinon téléchargé une seule fois).
    this.hydrate(video)

    video.muted = true

    const playing = video.play()

    if (playing) {
      playing.catch(() => {})
    }
  }

  pause (e) {
    const video = this.getVideo(e.currentTarget)

    if (!video || !video.src || video.paused) {
      return
    }

    // On laisse le fondu de sortie se terminer (opacity 0.4s) avant de figer
    // la lecture, pour ne pas geler l'image pendant la transition.
    video.videoPauseTimer = window.setTimeout(() => video.pause(), 500)
  }

  hydrate (video, preload) {
    if (!video.dataset.src) {
      return
    }

    if (preload) {
      video.preload = preload
    }

    video.src = video.dataset.src
    video.removeAttribute('data-src')
  }

  getVideo (container) {
    return container.querySelector('video.bg-video')
  }
}
